import React from 'react';
import { CAPABILITIES } from '@/lib/types/authorization';
import { useCan } from '@/lib/hooks/useCan';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AppLayout } from '@/components/layout/AppLayout';
import { patientsApi } from '@/lib/api/patients';
import type { PatientSearchParams } from '@/lib/types/patient';
import { formatDate } from '@/lib/types/patient';
import RecordsView from '@/components/views/RecordsView';
import { PatientFormModal } from '@/components/modals/PatientFormModal';
import { PatientFormContent } from '@/components/forms/PatientFormContent';
import {
  ScheduleAppointmentModal,
  type PatientData,
} from '@/components/modals/ScheduleAppointmentModal';
import { useAuthStore } from '@/lib/stores/authStore';
import { createComponentLogger } from '@/lib/utils/logger';
import { medplumResourcesApi } from '@/lib/api/medplum-resources';
import { startConsultationForPatient } from '@/lib/utils/encounter';

const logger = createComponentLogger('RecordsPage');

/**
 * RecordsPage Component
 *
 * Main page for viewing and managing patient records.
 *
 * Features:
 * - Paginated patient list with DataTable
 * - Search by name, identifier, or phone
 * - Filter by gender and status
 * - Sort by name, age, or creation date
 * - Navigate to patient detail or edit
 * - Create new patient
 * - Loading and empty states
 * - Error handling
 *
 * @accessibility
 * - Keyboard navigation for search and filters
 * - Screen reader support for table
 * - ARIA labels on interactive elements
 */
export function RecordsPage() {
  const canConsult = useCan(CAPABILITIES.CONSULTATION_START);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const projectConfig = useAuthStore((state) => state.projectConfig);
  const user = useAuthStore((state) => state.user);
  const role = user?.role;
  const canViewAllPatients = role === 'admin' || role === 'practitioner';
  const showDoctorFilter = role === 'admin';

  // Get addPatient from URL query params for Add Patient modal
  const addPatientFromQuery = searchParams.get('addPatient');
  // For edit mode in form modal, check if addPatient is true and patientId exists
  const patientIdFromQuery = searchParams.get('patientId');
  const isEditModeInModal = addPatientFromQuery === 'true' && patientIdFromQuery !== null;
  // Get prefillPhone for when switching from appointment search to patient creation
  const prefillPhoneFromQuery = searchParams.get('prefillPhone');

  // Schedule Appointment modal state
  const [scheduleModalPatient, setScheduleModalPatient] = React.useState<PatientData | null>(null);

  // Edit Patient modal state
  const [editPatientModalOpen, setEditPatientModalOpen] = React.useState(false);
  const [editingPatientId, setEditingPatientId] = React.useState<string | null>(null);

  // Consultation loading state
  const [isStartingConsultation, setIsStartingConsultation] = React.useState<string | null>(null);

  // Search and filter state
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedQuery, setDebouncedQuery] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize] = React.useState(10);

  // Tab state for role-based patient filtering (admin + practitioner)
  const [activeTab, setActiveTab] = React.useState<'my-patients' | 'all-patients'>('my-patients');
  // Doctor filter state for administrators
  const [selectedDoctorId, setSelectedDoctorId] = React.useState<string | undefined>(undefined);

  // Administrators and practitioners can switch between patient scopes.
  const showTabs = canViewAllPatients;

  // Debounce search query
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      setCurrentPage(1); // Reset to first page on search
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page when tab or doctor filter changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, selectedDoctorId]);

  // Fetch practitioner's patient IDs for "My Patients" tab
  // This queries all Encounters where the current practitioner participated,
  // then extracts unique patient IDs from those encounters
  const {
    data: practitionerPatientIds,
    isLoading: isPractitionerPatientIdsLoading,
    isError: isPractitionerPatientIdsError,
    refetch: refetchPractitionerPatientIds,
  } = useQuery({
    queryKey: ['practitioner-patients', user?.id],
    queryFn: () => medplumResourcesApi.getPractitionerPatientIds(user!.id),
    enabled: showTabs && activeTab === 'my-patients' && !!user?.id,
    staleTime: 60000, // 1 minute - patient list doesn't change often
    gcTime: 5 * 60 * 1000, // 5 minutes cache
  });

  // Fetch selected doctor's patient IDs for the administrator's "All Patients" tab
  const {
    data: selectedDoctorPatientIds,
    isLoading: isSelectedDoctorPatientIdsLoading,
    isError: isSelectedDoctorPatientIdsError,
    refetch: refetchSelectedDoctorPatientIds,
  } = useQuery({
    queryKey: ['practitioner-patients', selectedDoctorId],
    queryFn: () => medplumResourcesApi.getPractitionerPatientIds(selectedDoctorId!),
    enabled:
      showTabs && activeTab === 'all-patients' && !!selectedDoctorId && selectedDoctorId !== 'all',
    staleTime: 60000,
    gcTime: 5 * 60 * 1000,
  });

  // Fetch practitioners list for the administrator's doctor dropdown
  const { data: practitionersData } = useQuery({
    queryKey: ['practitioners-for-filter'],
    queryFn: async () => {
      return medplumResourcesApi.listPractitioners({ count: 100 });
    },
    enabled: showDoctorFilter,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Determine which patient IDs to filter by based on active tab and doctor selection
  const effectivePatientIds = React.useMemo(() => {
    if (!showTabs) {
      // Front desk users see all patients without patient-scope tabs.
      return undefined;
    }
    if (activeTab === 'my-patients') {
      // "My Patients" tab: filter to own patients
      return practitionerPatientIds;
    }
    // "All Patients" tab
    if (selectedDoctorId && selectedDoctorId !== 'all') {
      // An administrator selected a specific doctor: filter to that doctor's patients
      return selectedDoctorPatientIds;
    }
    // No doctor filter: show all patients
    return undefined;
  }, [showTabs, activeTab, practitionerPatientIds, selectedDoctorId, selectedDoctorPatientIds]);

  // Whether patient list fetch should be enabled
  const isPatientFetchEnabled = React.useMemo(() => {
    if (!showTabs) return true; // Non-tab users always fetch
    if (activeTab === 'my-patients')
      return practitionerPatientIds !== undefined || isPractitionerPatientIdsError;
    if (selectedDoctorId && selectedDoctorId !== 'all')
      return selectedDoctorPatientIds !== undefined || isSelectedDoctorPatientIdsError;
    return true; // "All Patients" with no doctor filter
  }, [
    showTabs,
    activeTab,
    practitionerPatientIds,
    isPractitionerPatientIdsError,
    selectedDoctorId,
    selectedDoctorPatientIds,
    isSelectedDoctorPatientIdsError,
  ]);

  // Build search params - leverage backend API for server-side search by name, phone, and identifier
  const patientSearchParams: PatientSearchParams = {
    page: debouncedQuery ? 1 : currentPage, // When searching, always fetch from page 1
    limit: pageSize, // Use normal pageSize - backend handles filtering
    query: debouncedQuery || undefined, // Pass query to API for server-side filtering by name, phone, and identifier
    sortBy: 'createdAt',
    sortOrder: 'desc',
    patientIds: effectivePatientIds,
  };

  // Fetch patients with React Query
  const {
    data: patientsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['patients', patientSearchParams],
    queryFn: () => patientsApi.list(patientSearchParams),
    staleTime: 30000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes cache
    refetchOnWindowFocus: false, // Don't refetch on window focus for smoother UX
    enabled: isPatientFetchEnabled, // Wait for patient IDs to load before fetching patients
  });

  // Combine loading and error states from dependent queries with the main query
  // This prevents an indefinite loading spinner if a dependent query fails
  const isDependentLoading =
    (showTabs &&
      activeTab === 'my-patients' &&
      isPractitionerPatientIdsLoading &&
      !isPractitionerPatientIdsError) ||
    (showTabs &&
      activeTab === 'all-patients' &&
      !!selectedDoctorId &&
      selectedDoctorId !== 'all' &&
      isSelectedDoctorPatientIdsLoading &&
      !isSelectedDoctorPatientIdsError);

  const dependentError =
    (showTabs && activeTab === 'my-patients' && isPractitionerPatientIdsError) ||
    (showTabs &&
      activeTab === 'all-patients' &&
      !!selectedDoctorId &&
      selectedDoctorId !== 'all' &&
      isSelectedDoctorPatientIdsError);

  // Combined loading and error states passed to the view
  const combinedIsLoading = isLoading || isDependentLoading;
  const combinedIsError = isError || dependentError;

  // Backend API handles all search types (name, phone, identifier) server-side
  // No client-side filtering needed - use API results directly
  const paginatedPatients = React.useMemo(() => {
    return patientsData?.patients || [];
  }, [patientsData?.patients]);

  // Calculate pagination info
  // Backend handles pagination for both search and non-search cases
  const hasPrev = patientsData?.pagination.hasPrev ?? false;
  const hasNext = patientsData?.pagination.hasNext ?? false;

  // Handle clear filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setCurrentPage(1);
  };

  // Handle tab change
  const handleTabChange = (tab: 'my-patients' | 'all-patients') => {
    setActiveTab(tab);
    if (tab === 'my-patients') {
      setSelectedDoctorId(undefined); // Clear doctor filter when switching to My Patients
    }
  };

  // Handle doctor filter change
  // RecordsView converts 'all' selection to undefined before calling this handler
  const handleDoctorFilterChange = (value: string | undefined) => {
    setSelectedDoctorId(value);
  };

  // Build practitioner options for the administrator's doctor dropdown
  // Filter out practitioners without a valid id to prevent React key warnings
  const doctorOptions = React.useMemo(() => {
    if (!practitionersData?.practitioners) return [];
    return practitionersData.practitioners
      .filter((practitioner) => !!practitioner.id)
      .map((practitioner) => {
        const name = practitioner.name?.[0];
        const displayName = name
          ? `${name.given?.join(' ') || ''} ${name.family || ''}`.trim()
          : practitioner.id || 'Unknown';
        return { id: practitioner.id!, name: displayName };
      });
  }, [practitionersData]);

  // Handle Add Patient - open modal
  const handleAddPatient = () => {
    setSearchParams({ addPatient: 'true' });
  };

  // Close form modal handler
  const handleCloseFormModal = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('addPatient');
    newParams.delete('prefillPhone');
    // Only delete patientId if we're in create mode (not edit mode)
    if (!isEditModeInModal) {
      newParams.delete('patientId');
    }
    setSearchParams(newParams);
  };

  // Handle switching from Add Patient form to Schedule Appointment
  const handleSwitchToAppointment = (patient: {
    id: string;
    name: string;
    phoneNumber: string;
    age?: number;
    gender: 'M' | 'F' | 'O' | 'U';
  }) => {
    handleCloseFormModal();
    setScheduleModalPatient({
      uuid: patient.id,
      name: patient.name,
      age: patient.age ?? 0,
      gender: patient.gender,
      phoneNumber: patient.phoneNumber,
    });
  };

  // Handle form submission success
  const handleFormSuccess = () => {
    handleCloseFormModal();
    // Refetch patients list to show the newly added patient
    refetch();
  };

  // Handle edit patient - open modal with patient ID
  const handleEditPatient = React.useCallback((patientUuid: string) => {
    setEditingPatientId(patientUuid);
    setEditPatientModalOpen(true);
  }, []);

  // Handle closing edit patient modal
  const handleCloseEditPatientModal = React.useCallback(() => {
    setEditPatientModalOpen(false);
    setEditingPatientId(null);
  }, []);

  // Handle edit patient success - close modal, refetch data
  const handleEditPatientSuccess = React.useCallback(() => {
    handleCloseEditPatientModal();
    // Refetch patients list to show updated patient info
    refetch();
  }, [handleCloseEditPatientModal, refetch]);

  // Handle start consultation for a patient
  // Finds or creates today's Appointment and a linked Encounter via the BFF, same
  // as the Dashboard's own "search & start" walk-in flow
  // (handleStartConsultationFromSearch in DashboardPage.tsx) — this is what makes the patient show up on the
  // Dashboard's "Today's Appointments" table (and therefore get the
  // Preparing summary / Pending verification / Verify treatment) instead of
  // vanishing after a Quick Submit with nowhere to display its status.
  const handleStartConsultation = async (patientUuid: string) => {
    if (!patientUuid) return;
    if (isStartingConsultation) return; // Prevent double-clicking

    const user = useAuthStore.getState().user;
    if (!user) {
      toast.error('User not authenticated');
      return;
    }

    // Show toast immediately on click
    toast.info('Preparing consultation...', { duration: 2000 });
    setIsStartingConsultation(patientUuid);

    try {
      // Finds or creates today's Appointment + Encounter together, server-side.
      const { encounterId } = await startConsultationForPatient(patientUuid);

      // So the row shows up immediately if the doctor navigates to the Dashboard
      queryClient.invalidateQueries({
        queryKey: ['dashboard-practitioner-appointments'],
        exact: false,
      });
      queryClient.invalidateQueries({ queryKey: ['appointments'], exact: false });

      logger.log('Navigating to consultation with encounter:', encounterId);
      toast.success('Ready for consultation');
      navigate(`/consultation/${encounterId}`);
    } catch (error) {
      logger.error('Failed to start consultation:', error);
      toast.error('Failed to start consultation. Please try again.');
    } finally {
      setIsStartingConsultation(null);
    }
  };

  return (
    <AppLayout>
      <RecordsView
        header={{
          title: 'Patients Records',
          subtitle: 'View and manage all patient records',
        }}
        search={{
          value: searchQuery,
          onChange: (v) => setSearchQuery(v),
          placeholder: 'Search Patient',
        }}
        data={{
          rows: paginatedPatients.map((p) => ({
            uuid: p.uuid,
            identifier: p.identifier,
            name: p.name,
            age: p.age,
            gender: p.gender,
            phoneNumber: p.phoneNumber,
            status: p.status,
            lastUpdated: p.lastUpdated ? formatDate(p.lastUpdated) : null,
          })),
        }}
        isLoading={combinedIsLoading}
        error={
          combinedIsError
            ? error instanceof Error
              ? error.message
              : 'Failed to load patients. Please try again.'
            : null
        }
        onRetry={() => {
          if (isPractitionerPatientIdsError) refetchPractitionerPatientIds();
          if (isSelectedDoctorPatientIdsError) refetchSelectedDoctorPatientIds();
          refetch();
        }}
        onRefresh={() => refetch()}
        actions={{
          onCreatePatient:
            projectConfig?.services.addPatient === false ? undefined : handleAddPatient,
          onRowClick: (uuid) => navigate(`/patient-record/${uuid}`),
          onClearFilters: handleClearFilters,
          onStartConsultation: handleStartConsultation,
          onScheduleAppointment: (patient) => setScheduleModalPatient(patient),
          onEditPatient: handleEditPatient,
        }}
        isStartingConsultation={isStartingConsultation}
        showEditPatientButton={true}
        showConsultationButton={canConsult}
        tabs={showTabs ? { activeTab, onTabChange: handleTabChange } : undefined}
        doctorFilter={
          showDoctorFilter
            ? {
                value: selectedDoctorId,
                onChange: handleDoctorFilterChange,
                options: doctorOptions,
              }
            : undefined
        }
        pagination={{
          page: currentPage,
          pageSize,
          total: patientsData?.pagination.total || 0,
          hasPrev,
          hasNext,
          onPrev: () => setCurrentPage((prev) => Math.max(1, prev - 1)),
          onNext: () => setCurrentPage((prev) => prev + 1),
        }}
      />

      {/* Patient Form Modal */}
      {addPatientFromQuery === 'true' && (
        <PatientFormModal isOpen={true} onClose={handleCloseFormModal}>
          <PatientFormContent
            patientId={isEditModeInModal ? patientIdFromQuery : undefined}
            onSuccess={handleFormSuccess}
            onCancel={handleCloseFormModal}
            isModal={true}
            onSwitchToAppointment={handleSwitchToAppointment}
            prefillPhone={prefillPhoneFromQuery || undefined}
          />
        </PatientFormModal>
      )}

      {/* Schedule Appointment Modal */}
      <ScheduleAppointmentModal
        isOpen={!!scheduleModalPatient}
        onClose={() => setScheduleModalPatient(null)}
        patient={scheduleModalPatient}
        onAppointmentCreated={() => {
          // Optionally refetch or show success
          refetch();
        }}
      />

      {/* Edit Patient Modal */}
      <PatientFormModal isOpen={editPatientModalOpen} onClose={handleCloseEditPatientModal}>
        <PatientFormContent
          patientId={editingPatientId || undefined}
          onSuccess={handleEditPatientSuccess}
          onCancel={handleCloseEditPatientModal}
          isModal={true}
        />
      </PatientFormModal>
    </AppLayout>
  );
}
